/**
 * Paso Audiencia del asistente, pestaña «Subir un Excel».
 *
 * Lo principal (pedido de Alan del 27/10/2026: «yo te cargo y, si hace match con alguno del público, que me lo
 * pongas… así te puedo mandar la segmentación exacta»): se sube un Excel o un CSV ahí mismo, el backend reconoce solo
 * las columnas de celular, mail e id del cliente y lo cruza con el Público (`POST /audiencias/publico/listas`). Con
 * «Mandarle a estas personas» la campaña pasa a «Del Público de Activa» con esa lista: a los que ya están se les
 * escribe con su nombre, su consentimiento y sus datos, con las compuertas de siempre.
 *
 * Los que no están en el Público se pueden cargar a la Base con su consentimiento: la lista se cruza cada vez, así que
 * entran solos cuando el Público se vuelve a armar. Y queda la otra salida de antes (26/10/2026): cargar el archivo
 * entero como contactos nuevos y volver con su público (`?volverA=`).
 */

import { useId, useState } from "react";
import { Link } from "react-router-dom";
import { FileSpreadsheet, Loader2, Search, Send, Upload } from "lucide-react";
import type { ListaPublico } from "../types";
import { oficialApi } from "../api";
import { leerError, leerRespuesta, textoError } from "../logic/respuesta";
import { validarArchivoLocal } from "../logic/base";
import { prepararCarga } from "../logic/archivo";
import { num, plural } from "../../campana-nueva/utils/format";
import { R_CARGAS } from "./base/comun";

/** La carga de contactos con la vuelta a la campaña. */
export function rutaSubirExcel(volverA: string): string {
  return `${R_CARGAS}/nueva?volverA=${encodeURIComponent(volverA)}`;
}

/** «Celular y Mail»: las columnas de las que salieron las llaves, como las titula el archivo. */
export function columnasUsadas(l: Pick<ListaPublico, "columnas">): string {
  const todas = [...l.columnas.telefono, ...l.columnas.email, ...l.columnas.id].map((c) => `«${c}»`);
  return todas.length < 2 ? todas.join("") : `${todas.slice(0, -1).join(", ")} y ${todas[todas.length - 1]}`;
}

interface Props {
  /** Ruta del asistente, para volver después de cargar el archivo entero como contactos nuevos. */
  volverA: string;
  /** Mandarle a las personas del Público que están en el archivo: el asistente pasa a «Del Público de Activa» con la lista. */
  onUsarLista: (l: ListaPublico) => void;
}

export function AudienciaExcel({ volverA, onUsarLista }: Props) {
  const idArchivo = useId();
  const [archivo, setArchivo] = useState<File | null>(null);
  const [errorArchivo, setErrorArchivo] = useState("");
  const [leyendo, setLeyendo] = useState(false);
  const [error, setError] = useState("");
  const [lista, setLista] = useState<ListaPublico | null>(null);

  const elegir = (f: File | null | undefined) => {
    setLista(null);
    setError("");
    if (!f) return;
    const v = validarArchivoLocal(f.name, f.size);
    if (!v.ok) { setArchivo(null); setErrorArchivo(v.error); return; }
    setErrorArchivo("");
    setArchivo(f);
  };

  const buscar = async () => {
    if (!archivo || leyendo) return;
    setLeyendo(true);
    setError("");
    try {
      const c = await prepararCarga(archivo);
      const l = leerRespuesta<ListaPublico>(await oficialApi.crearListaPublico({ nombreArchivo: c.nombreArchivo, archivo: c.archivo }));
      if (!l.ok || !l.data) { setError(l.ok ? "El servidor no devolvió la lista." : l.error || textoError(l)); return; }
      setLista(l.data);
    } catch (e) {
      setError(e instanceof Error && e.message && !("status" in e) ? e.message : textoError(leerError(e)));
    } finally {
      setLeyendo(false);
    }
  };

  return (
    <div className="space-y-4 text-sm text-slate-600">
      <div className="rounded-xl border border-slate-200 p-5 space-y-3">
        <p className="flex items-center gap-2 font-semibold text-slate-900">
          <FileSpreadsheet size={16} className="text-emerald-600" /> Mandale a la lista de tu archivo
        </p>
        <p>
          Subí un Excel (.xlsx o .xls) o un CSV con un celular, un mail o el id del cliente por fila. Lo cruzamos con tu
          Público: a los que ya están les escribimos con su nombre, su consentimiento y sus datos.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor={idArchivo} className="sr-only">Archivo de la lista</label>
          <input id={idArchivo} type="file" accept=".xlsx,.xls,.csv" onChange={(e) => elegir(e.target.files?.[0])}
            className="text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-slate-700" />
          <button type="button" onClick={() => void buscar()} disabled={!archivo || leyendo}
            className="inline-flex h-10 items-center gap-2 rounded-xl bg-violet-600 px-4 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50">
            {leyendo ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />} {leyendo ? "Buscando…" : "Buscar en mi Público"}
          </button>
        </div>
        {errorArchivo && <p className="text-red-700" role="alert">{errorArchivo}</p>}
        {error && <p className="text-red-700" role="alert">{error}</p>}

        {lista && (
          <div className="rounded-xl bg-slate-50 px-4 py-3 space-y-2" data-testid="resultado-lista" aria-live="polite">
            <p className="text-slate-800">
              De {num(lista.filas)} {plural(lista.filas, "fila", "filas")} de «{lista.nombreArchivo || lista.nombre}»,{" "}
              <strong>{num(lista.filasEnElPublico)}</strong> {plural(lista.filasEnElPublico, "es", "son")} de{" "}
              <strong>{num(lista.personasEnElPublico)}</strong> {plural(lista.personasEnElPublico, "persona", "personas")} de tu Público.
            </p>
            <p className="text-xs text-slate-500">Cruzamos por {columnasUsadas(lista)}, exactos: un celular o un mail tiene que ser el mismo.</p>
            {lista.sinDato > 0 && (
              <p className="text-xs text-slate-500">
                {num(lista.sinDato)} {plural(lista.sinDato, "fila no tiene", "filas no tienen")} un celular, un mail ni un id de cliente que sirva.
              </p>
            )}
            {lista.filasQueNoEstan > 0 && (
              <p className="text-xs text-slate-600" data-testid="lista-no-estan">
                {num(lista.filasQueNoEstan)} no {plural(lista.filasQueNoEstan, "está", "están")} en tu Público. Si dieron su consentimiento,{" "}
                <Link to={`${R_CARGAS}/nueva`} target="_blank" rel="noopener" className="font-semibold text-violet-700 underline">cargalas como contactos nuevos</Link>:
                cuando el Público se vuelva a armar (solo, cada 30 minutos) entran solas en esta lista.
              </p>
            )}
            {lista.personasEnElPublico > 0 ? (
              <button type="button" onClick={() => onUsarLista(lista)}
                className="inline-flex h-10 items-center gap-2 rounded-xl bg-violet-600 px-4 text-sm font-semibold text-white hover:bg-violet-700">
                <Send size={15} /> Mandarle a {plural(lista.personasEnElPublico, "esta persona", `estas ${num(lista.personasEnElPublico)} personas`)}
              </button>
            ) : (
              <p className="text-amber-800">Nadie del archivo está en tu Público: cargalos como contactos nuevos o revisá que sea la lista correcta.</p>
            )}
            <p className="text-xs text-slate-500">Antes de mandar vas a ver cuántas pueden recibir: sale sólo quien dijo que sí, como siempre.</p>
          </div>
        )}
      </div>

      <div className="rounded-xl border-2 border-dashed border-slate-200 p-5 space-y-2">
        <p className="font-semibold text-slate-900">¿Son contactos nuevos?</p>
        <p>
          Cargá el archivo entero a la Base, contando cómo dieron su consentimiento. Al terminar, «Usar en la campaña» te
          trae de vuelta con esos contactos elegidos, y quedan para la próxima.
        </p>
        <Link to={rutaSubirExcel(volverA)}
          className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50">
          <Upload size={15} /> Cargar como contactos nuevos
        </Link>
      </div>
    </div>
  );
}

export default AudienciaExcel;
